import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable, map} from 'rxjs';
import {REMIT_BASE_URL} from './config';
import {RemitApiResponse} from './kyc/user-verification.service';

export interface UpdateBeneficiaryRulesParams{
  reason: string
  limitTrxNumber: string
}

export type CreateBeneficiaryRulesParams = {
  bankCode: string
  accountNumber: string
} & UpdateBeneficiaryRulesParams

export interface BeneficiaryRules {
  body: BeneficiaryRule[]
  totalData: number;
}

export type BeneficiaryRule = CreateBeneficiaryRulesParams & {
  id: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  version: string;
}

export interface BeneficiaryRulesQueryParams {
  pageNumber: number;
  pageSize: number;
  bankCode?: string | null;
  accountNumber?: string | null;
}

export interface RefreshBeneficiaryResponse {
  message: string;
  accountId: string;
  accountName: string;
  status: 'available' | 'unavailable';
  userId: string;
}

@Injectable({
  providedIn: 'root',
})
export class RemitBeneficiariesService {
  constructor(
    private http: HttpClient,
    @Inject(REMIT_BASE_URL) private remitApiUrl: string,
  ) { }


  createBenefRule(params: CreateBeneficiaryRulesParams):
  Observable<RemitApiResponse> {
    return this.http.post<RemitApiResponse>(
        this.remitApiUrl + '/staff/beneficiary-rule',
        params,
    );
  }

  updateBenefRule(id: string, params: Partial<UpdateBeneficiaryRulesParams>):
  Observable<RemitApiResponse> {
    return this.http.put<RemitApiResponse>(
        this.remitApiUrl + `/staff/beneficiary-rule/${id}`,
        params,
    );
  }

  getBenefRule(id: BeneficiaryRule):
  Observable<BeneficiaryRule> {
    return this.http.get<{body: BeneficiaryRule}>(
        this.remitApiUrl + `/staff/beneficiary-rule/${id}`,
    ).pipe(map((v)=> v.body));
  }

  queryBenefRule(params: Partial<BeneficiaryRulesQueryParams>):
  Observable<BeneficiaryRules> {
    return this.http.post<BeneficiaryRules>(
        this.remitApiUrl + `/staff/beneficiary-rules`,
        params,
    );
  }

  /**
   * Refresh beneficiary account information by staff
   * Requires Customer Service permissions (auth.PermissionRemitCS)
   *
   * @param {string} id The unique identifier of the beneficiary account to refresh
   * @return {Observable<RefreshBeneficiaryResponse>} Updated account information
   */
  refreshBeneficiaryAccount(id: string): Observable<RefreshBeneficiaryResponse> {
    return this.http.post<RefreshBeneficiaryResponse>(
        `${this.remitApiUrl}/staff/beneficiaries/${id}/refresh`,
        {}, // Empty body as per API specification
    );
  }
}
